import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, TEST_API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";
import { useBranch } from 'contexts/BranchContext';

// CSS imports
import 'styles/Font.css';

// Components imports
import Footer from "components/layout/Footer/Footer";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import BottomSheet from 'components/ui/BottomSheet/BottomSheet';
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// Image imports
import dotIcon from 'assets/images/icon/dot-icon.svg';
import starIcon from 'assets/images/icon/star-icon.svg';
import crownIcon from 'assets/images/icon/crown-icon.svg';
import clockIcon from 'assets/images/icon/clock-icon.svg';
import calendarIcon from 'assets/images/icon/calendar-icon.svg';
import arrowRightIcon from 'assets/images/icon/arrow-right-icon.svg';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';


// --- 날짜 유틸리티 함수 ---
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const cleaned = dateStr.replace(/[^0-9]/g, '');
    if (cleaned.length === 8) {
        return `${cleaned.substring(0, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}`;
    }
    return dateStr;
};

// 이용 기간 확인 (오늘 날짜가 종료일보다 같거나 이전이면 true)
const checkIsActive = (edate) => {
    if (!edate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = edate.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) return false;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));
    const endDate = new Date(year, month, day);
    endDate.setHours(0, 0, 0, 0);

    return endDate >= today;
};

// 잔여 기간 계산 함수
const calculateRemainDays = (edate) => {
    if (!edate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cleaned = edate.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) return null;

    const year = parseInt(cleaned.substring(0, 4));
    const month = parseInt(cleaned.substring(4, 6)) - 1;
    const day = parseInt(cleaned.substring(6, 8));
    const endDate = new Date(year, month, day);
    endDate.setHours(0, 0, 0, 0);

    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : null;
};


// --- 수업 카드 컴포넌트 ---
const ClassCard = ({ exercise, date, onClick, onTimeClick }) => {
    return (
        <div style={scheduleStyles.classCard} onClick={() => onClick && onClick(exercise)}>
            <div style={scheduleStyles.classInfoMain}>
                <img
                    src={exercise.img || defaultCoachImg}
                    alt="수업"
                    style={scheduleStyles.classImg}
                />
                <div style={scheduleStyles.classTextContent}>
                    <p style={scheduleStyles.className}>{exercise.exerciseName}</p>
                    <p style={scheduleStyles.classIntro}>{exercise.intro}</p>
                </div>
            </div>

            <div style={scheduleStyles.timeSlotList}>
                {exercise.times && exercise.times.map((t, idx) => (
                    <div
                        key={t.stKey ? `slot-${t.stKey}-${idx}` : `idx-${idx}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (t.isAvailable === 'Y') {
                                onTimeClick && onTimeClick(exercise.ctKey, date, t.time);
                            }
                        }}
                        style={{
                            ...scheduleStyles.timeSlot,
                            ...(t.isAvailable === 'N' ? scheduleStyles.timeSlotDisabled : {})
                        }}
                    >
                        <span style={scheduleStyles.timeSlotText}>
                            {t.time} {t.isAvailable === 'Y' ? `(${t.totalCnt - t.remCnt}/${t.totalCnt})` : '(마감)'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- 날짜 섹션 컴포넌트 ---
const DaySection = ({ day, onClassClick, onTimeClick }) => {
    // 날짜 포맷팅 (2026-05-04 -> 5. 4)
    const formatDateLabel = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split('-');
        if (parts.length < 3) return dateStr;
        return `${parseInt(parts[1])}. ${parseInt(parts[2])}`;
    };

    return (
        <div style={scheduleStyles.dayContainer}>
            <div style={scheduleStyles.dayHeader}>
                <span style={scheduleStyles.dayLabel}>{formatDateLabel(day.date)} ({day.dayName})</span>
            </div>
            <div style={scheduleStyles.classList}>
                {day.exercises && day.exercises.map((ex, idx) => (
                    <ClassCard
                        key={ex.ctKey ? `class-${ex.ctKey}-${idx}` : `idx-${idx}`}
                        exercise={ex}
                        date={day.date}
                        onClick={onClassClick}
                        onTimeClick={onTimeClick}
                    />
                ))}
            </div>
        </div>
    );
};


// --- 코치 카드 컴포넌트 ---
const CoachCard = ({ coach, onClick }) => (
    <div className="PtCoachCard" style={staticStyles.ptCoachCard} onClick={() => onClick(coach)}>
        <div style={staticStyles.ptCoachCardImageContainer}>
            <img className="PtCoachCardImage" src={coach.img || defaultCoachImg} alt={`${coach.coachName} 사진`}
                style={staticStyles.ptCoachCardImage} />
        </div>
        <div style={staticStyles.ptCoachCardInfo}>
            <p style={staticStyles.coachName}>{coach.coachName || coach.name || '트레이너 이름'}</p>
            <p style={staticStyles.coachIntroduction}>{coach.cont || coach.introduction || ""}</p>
            <div style={staticStyles.coachReview}>
                <div style={staticStyles.starDiv}><img src={starIcon} alt="별점" style={staticStyles.starIcon} /><span
                    style={staticStyles.rating}>{!isNaN(Number(coach.rating)) ? Number(coach.rating).toFixed(1) : '0.0'}</span>
                </div>
                <img src={dotIcon} alt="dot" style={staticStyles.dotIcon} /><span
                    style={staticStyles.reviewCount}>리뷰 {coach.reviewCnt || coach.reviews || 0}개</span>
            </div>
        </div>
    </div>
);


// --- 나의 예약 카드 컴포넌트 ---
const MyReserveCard = ({ item, onClick }) => (
    <div style={myReserveStyles.card} onClick={() => onClick && onClick(item)}>
        <img src={item.img || defaultCoachImg} alt="코치" style={myReserveStyles.coachImg} />
        <div style={myReserveStyles.infoContainer}>
            <span style={myReserveStyles.coachName}>{item.coachName}</span>
            <div style={myReserveStyles.dateTimeInfo}>
                <div style={myReserveStyles.dateWrapper}>
                    <img src={calendarIcon} alt="날짜" style={myReserveStyles.icon} />
                    <span style={myReserveStyles.dateText}>{item.date}</span>
                </div>
                <img src={dotIcon} alt="dot" style={myReserveStyles.dotDivider} />
                <div style={myReserveStyles.timeWrapper}>
                    <img src={clockIcon} alt="시간" style={myReserveStyles.icon} />
                    <span style={myReserveStyles.timeText}>{item.time}</span>
                </div>
            </div>
        </div>
    </div>
);

// 나의 예약 목록 섹션
const MyReserveSection = ({ list, onMoreClick }) => {
    return (
        <div style={myReserveStyles.container}>
            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            <div style={myReserveStyles.titleWrapper} onClick={onMoreClick}>
                <span style={myReserveStyles.title}>예약 중인 수업</span>
                <img src={arrowRightIcon} alt="더보기" style={myReserveStyles.arrowIcon} />
            </div>

            <div className="hide-scrollbar" style={myReserveStyles.listContainer}>
                {list && list.length > 0 ? (
                    list.map((res) => (
                        <MyReserveCard
                            key={res.id}
                            item={res}
                        />
                    ))
                ) : (
                    <div style={{ ...myReserveStyles.card, justifyContent: 'center', color: '#747474' }}>
                        예약된 내역이 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
};

// 이전 이용 내역 섹션
const PastReserveSection = ({ list }) => {
    return (
        <div style={myReserveStyles.container}>
            <div style={myReserveStyles.titleWrapper}>
                <span style={myReserveStyles.title}>이전 이용 내역</span>
            </div>

            <div className="hide-scrollbar" style={myReserveStyles.listContainer}>
                {list && list.length > 0 ? (
                    list.map((res) => (
                        <MyReserveCard
                            key={res.id}
                            item={res}
                        />
                    ))
                ) : (
                    <div style={{ ...myReserveStyles.card, justifyContent: 'center', color: '#747474' }}>
                        이전 이용 내역이 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
};


function Pt() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    const navigate = useNavigate();
    const { currentBranch } = useBranch();  // 전역 지점 정보 가져오기

    const brKey = currentBranch.brKey;
    const brName = currentBranch.brName;
    const stKind = 'C'; // PT 페이지이므로 고정하거나 props로 받음
    const [myPtList, setMyPtList] = useState([]); // 전체 예약 목록
    const [activeTab, setActiveTab] = useState('PT');
    const [mySubscriptionList, setMySubscriptionList] = useState([]); // 구독 목록 상태

    const [coachList, setCoachList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [scheduleList, setScheduleList] = useState([]);
    const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
    const [showSheet, setShowSheet] = useState(false);
    const [sheetTimes, setSheetTimes] = useState([]);
    const [currentSelectedDate, setCurrentSelectedDate] = useState("");
    const [selectedCtKey, setSelectedCtKey] = useState("");
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingReservation, setPendingReservation] = useState(null);
    const [slickStatus, setSlickStatus] = useState('N');
    const [initialTimeData, setInitialTimeData] = useState(null);
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: ''
    });

    // --- API 호출: 사용자 상태(slickStatus) 조회를 위해 /home 호출 ---
    useEffect(() => {
        const fetchUserStatus = async () => {
            const { token } = getTokenStatus();
            if (!token) return;

            try {
                const response = await fetch(`${API_BASE_URL}/home`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({ brKey: brKey })
                });

                if (response.ok) {
                    const data = await response.json();
                    const resData = data.data || data;
                    if (resData.slickStatus) {
                        setSlickStatus(resData.slickStatus);
                    }
                }
            } catch (e) {
                console.error("Failed to fetch user status:", e);
            }
        };

        fetchUserStatus();
    }, [brKey]);

    // --- API 호출: PT 수업 목록 조회 ---
    useEffect(() => {
        if (activeTab !== 'PT') return;
        const { token } = getTokenStatus();
        const fetchClassList = async () => {
            setIsLoadingSchedule(true);
            try {
                const queryParams = new URLSearchParams({
                    brKey: brKey,
                    stKind: stKind,
                    weekOffset: 0
                }).toString();

                const CLASS_LIST_API_URL = `${API_BASE_URL}/weekSchedule?${queryParams}`;

                const response = await fetch(CLASS_LIST_API_URL, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                const data = await response.json();
                const resData = data.data || data;

                if (resData.list && Array.isArray(resData.list)) {
                    // 오늘 날짜 구하기 (YYYY-MM-DD 포맷)
                    const now = new Date();
                    const year = now.getFullYear();
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const date = String(now.getDate()).padStart(2, '0');
                    const todayStr = `${year}-${month}-${date}`;

                    // 지난 날짜(과거) 및 수업이 없는 날짜는 제외하고 필터링
                    const filteredList = resData.list.filter(day =>
                        day.date >= todayStr && day.exercises && day.exercises.length > 0
                    );
                    setScheduleList(filteredList);
                } else {
                    setScheduleList([]);
                }
            } catch (e) {
                console.error("❌ PT 수업 목록 조회 에러:", e);
                setScheduleList([]);
            } finally {
                setIsLoadingSchedule(false);
            }
        };
        fetchClassList();
    }, [activeTab, brKey, stKind]);

    // --- Handler: 날짜 변경 핸들러 (API 호출) ---
    const handleDateChange = useCallback(async (date, targetCtKey) => {
        const ctKeyToUse = targetCtKey || selectedCtKey;
        setCurrentSelectedDate(date);
        const { token } = getTokenStatus();
        if (!token || !ctKeyToUse) return;

        try {
            const SCHEDULE_API_URL = `${API_BASE_URL}/schedule?brKey=${brKey}&ctKey=${ctKeyToUse}&stKind=${stKind}&date=${date}`;
            const response = await fetch(SCHEDULE_API_URL, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const resData = await response.json();
            const data = resData.data || resData;

            let times = [];
            if (data.list && Array.isArray(data.list)) {
                times = data.list;
            } else if (data.mnInfo && data.mnInfo.times) {
                times = data.mnInfo.times;
            } else if (data.times) {
                times = data.times;
            }

            setSheetTimes(times);
        } catch (e) {
            console.error("Schedule Fetch Error:", e);
            setSheetTimes([]);
        }
    }, [selectedCtKey, brKey, stKind]);

    const handleTimeSlotClick = useCallback(async (ctKey, date, timeStr) => {
        if (slickStatus !== 'Y') {
            setAlertModal({
                isOpen: true,
                title: '예약 불가',
                message: `[${brName}] 수업 예약 권한이 없습니다.\n(이용권을 구매해주세요)`
            });
            return;
        }

        // 시간 포맷 변환 (예: "14:00" -> "오후 2:00")
        if (timeStr) {
            const parts = timeStr.split(':');
            let hour = parseInt(parts[0], 10);
            const minute = parts[1];
            const period = hour >= 12 ? '오후' : '오전';
            let displayHour = hour > 12 ? hour - 12 : hour;
            if (displayHour === 0) displayHour = 12;
            const formattedTime = `${period} ${displayHour}:${minute}`;
            setInitialTimeData({ time: formattedTime });
        } else {
            setInitialTimeData(null);
        }

        setSelectedCtKey(ctKey);
        await handleDateChange(date, ctKey);
        setShowSheet(true);
    }, [handleDateChange, slickStatus, brName]);

    const handleSheetConfirm = useCallback((data) => {
        setPendingReservation(data);
        setIsConfirmModalOpen(true);
    }, []);

    const handleFinalReservation = useCallback(async () => {
        if (!pendingReservation) return;

        const { stKey } = pendingReservation;
        const { token } = getTokenStatus();

        const payload = {
            brKey: brKey,
            stKey: String(stKey),
            stKind: stKind
        };

        try {
            const response = await fetch(`${API_BASE_URL}/regSchedule`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const resData = await response.json();
            const code = resData.data?.code || resData.code;
            const isSuccess = code === 200 || code === '200' || code === '0000';

            if (isSuccess) {
                setIsConfirmModalOpen(false);
                setShowSheet(false);
                alert("예약이 완료되었습니다.");
                // 예약 완료 후 화면 갱신
                if (activeTab === 'PT') {
                    const fetchClassList = async () => {
                        setIsLoadingSchedule(true);
                        try {
                            const queryParams = new URLSearchParams({
                                brKey: brKey,
                                stKind: stKind,
                                weekOffset: 0
                            }).toString();
                            const response = await fetch(`${API_BASE_URL}/weekSchedule?${queryParams}`, {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/x-www-form-urlencoded',
                                    'Authorization': `Bearer ${token}`,
                                }
                            });
                            const data = await response.json();
                            const resData = data.data || data;
                            if (resData.list && Array.isArray(resData.list)) {
                                const now = new Date();
                                const year = now.getFullYear();
                                const month = String(now.getMonth() + 1).padStart(2, '0');
                                const date = String(now.getDate()).padStart(2, '0');
                                const todayStr = `${year}-${month}-${date}`;
                                const filteredList = resData.list.filter(day =>
                                    day.date >= todayStr && day.exercises && day.exercises.length > 0
                                );
                                setScheduleList(filteredList);
                            }
                        } catch (e) {
                            console.error(e);
                        } finally {
                            setIsLoadingSchedule(false);
                        }
                    };
                    fetchClassList();
                }
            } else {
                const errorMsg = resData.data?.message || resData.message || '예약에 실패했습니다.';
                alert(errorMsg);
                setIsConfirmModalOpen(false);
                setShowSheet(false);
            }
        } catch (e) {
            console.error("Reservation Error:", e);
            alert("예약 중 오류가 발생했습니다.");
            setIsConfirmModalOpen(false);
            setShowSheet(false);
        }
    }, [pendingReservation, brKey, stKind, activeTab]);

    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);


    // 더보기 클릭 핸들러
    const handleMoreReservations = useCallback(() => {
        navigate('/sb/reserve');
    }, [navigate]);

    // --- API 호출: PT 트레이너 목록 조회 ---
    useEffect(() => {
        const { token } = getTokenStatus();
        const fetchPtList = async () => {
            setIsLoading(true);
            try {
                const queryParams = new URLSearchParams({
                    brKey: brKey,
                    stKind: stKind
                }).toString();

                const MANAGERLIST_API_URL = `${API_BASE_URL}/manager?${queryParams}`;

                const response = await fetch(MANAGERLIST_API_URL, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`,
                    }
                });

                if (!response.ok) {
                    throw new Error(`API Error: ${response.status}`);
                }

                const data = await response.json();
                const resData = data.data || data;

                let rawList = [];
                if (resData.mnInfo && Array.isArray(resData.mnInfo)) {
                    rawList = resData.mnInfo;
                } else if (resData.list && Array.isArray(resData.list)) {
                    rawList = resData.list;
                }

                // coachName (또는 name)이 있는 데이터만 필터링
                const filteredList = rawList.filter(item => {
                    const name = item.coachName || item.name;
                    return name && name.trim() !== ''; // null, undefined, 빈 문자열 제외
                });

                if (filteredList.length > 0) {
                    setCoachList(filteredList);
                } else {
                    console.warn("⚠️ 강사 목록 없음");
                    setCoachList([]);
                }

            } catch (error) {
                console.error("Boost List Fetch Error:", error);
                setCoachList([]);
            } finally {
                setIsLoading(false);
            }
        };

        if (activeTab === 'PT') {
            fetchPtList();
        }

    }, [activeTab, brKey, stKind]);


    // --- 상세 페이지 이동 핸들러 ---
    const handleCoachClick = useCallback((coach) => {
        const mnKey = coach.mnKey || coach.id;
        navigate('/pt/detail', {
            state: {
                mnKey: mnKey,
                stKind: stKind,
                trainer: coach,
            }
        });
    }, [navigate, stKind]);

    // ▼▼▼ [유지] PT 진행률 카드 컴포넌트 ▼▼▼
    const MySubscriptionCard = ({ item }) => {
        const hasCount = item.totalCnt !== null && item.totalCnt !== undefined && item.totalCnt > 0;
        const hasPeriod = item.period && item.period !== '';

        return (
            <div style={staticStyles.mySubProdBox}>
                <div style={staticStyles.mySubProdTitle}>
                    <div style={staticStyles.mySubProdName}>
                        {item.productName}
                        <img alt="왕관" src={crownIcon} style={staticStyles.crownIcon} />
                        <div style={staticStyles.mySubState}>{item.status}</div>
                    </div>

                    <div style={staticStyles.cardDivider} />

                    {hasPeriod && (
                        <div style={staticStyles.subPeriod}>
                            이용 기간
                            <span style={staticStyles.subPeriodValue}>{item.period}</span>
                        </div>
                    )}

                    {item.remainDays !== null && item.remainDays !== undefined && (
                        <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                            잔여 기간
                            <span style={staticStyles.subPeriodValue}>잔여 {item.remainDays}일</span>
                        </div>
                    )}

                    {hasCount && (
                        <>
                            <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                                이용 횟수
                                <span style={staticStyles.subPeriodValue}>
                                    {item.useCnt} / {item.totalCnt}회
                                </span>
                            </div>
                            <div style={{ ...staticStyles.subPeriod, marginTop: '8px' }}>
                                잔여 횟수
                                <span style={staticStyles.subPeriodValue}>
                                    잔여 {item.totalCnt + item.useCnt}회
                                </span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };
    // ▲▲▲ ----------------------------------- ▲▲▲

    // --- API 호출: 나의 예약 내역 조회 ---
    useEffect(() => {
        if (activeTab !== 'MY_PT') return;

        const { token } = getTokenStatus();
        const fetchReserveList = async () => {
            setIsLoading(true);
            try {
                const RESERVE_API_URL = `${API_BASE_URL}/reserve`;

                const response = await fetch(RESERVE_API_URL, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error(`API Error: ${response.status}`);

                const data = await response.json();
                const resData = data.data || data;
                let rawList = resData.list;
                if (!rawList) {
                    setMyPtList([]);
                } else {
                    if (!Array.isArray(rawList)) rawList = [rawList];

                    // stKind가 'E'인 항목만 필터링
                    const PtOnlyList = rawList.filter(item => item.stKind === 'C');

                    const formattedList = PtOnlyList.map((item) => ({
                        id: item.shKey,
                        date: item.date,
                        time: item.time,
                        coachName: item.mnName || '담당 트레이너',
                        img: item.img || defaultCoachImg,
                        status: item.status || '예약확정',
                        centerName: brName,
                        stKind: item.stKind
                    }));

                    setMyPtList(formattedList);

                    // 아래 중복 로직 제거 (setMySubscriptionList)
                    // /oneOrder API가 별도로 진행 정보를 가져오므로 여기서는 예약 리스트만 처리합니다.
                }
            } catch (error) {
                console.error("Reserve List Fetch Error:", error);
                setMyPtList([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReserveList();

    }, [activeTab, brName]);

    // ▼▼▼ [수정] 탭 변경 시 구독(OneOrder) API 호출 및 데이터 세팅 ▼▼▼
    useEffect(() => {
        if (activeTab === 'MY_PT') {
            const fetchOneOrder = async () => {
                const { token } = getTokenStatus();
                try {
                    const response = await fetch(`${API_BASE_URL}/oneOrder`, {
                        // const response = await fetch(`${TEST_API_BASE_URL}/oneOrder`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'Authorization': `Bearer ${token}`
                        },
                        // [필수] PT는 pdKind='C'
                        body: new URLSearchParams({ pdKind: 'C' })
                    });

                    if (response.ok) {
                        const data = await response.json();
                        const resData = data.data || data;
                        const orderInfo = resData.info || resData.data?.info;

                        if (orderInfo) {
                            // 날짜 포맷팅 및 기간 체크
                            const sdate = formatDate(orderInfo.sdate);
                            const edate = formatDate(orderInfo.edate);
                            const isActive = orderInfo.edate ? checkIsActive(orderInfo.edate) : true;
                            const remainDays = calculateRemainDays(orderInfo.edate);

                            // 기간 표시 문자열
                            const periodStr = (sdate && edate) ? `${sdate} ~ ${edate}` : '';
                            const statusStr = isActive ? '이용 중' : '기간 만료';

                            setMySubscriptionList([{
                                id: orderInfo.odKey || 'sub-pt',
                                coachName: orderInfo.coachName || '',
                                productName: orderInfo.name || 'PT 이용권',
                                useCnt: (orderInfo.useCnt !== undefined && orderInfo.useCnt !== null) ? Number(orderInfo.useCnt) : null,
                                totalCnt: (orderInfo.totalCnt !== undefined && orderInfo.totalCnt !== null) ? Number(orderInfo.totalCnt) : null,
                                status: statusStr,
                                period: periodStr,
                                remainDays: remainDays,
                            }]);
                        } else {
                            setMySubscriptionList([]);
                        }
                    } else {
                        setMySubscriptionList([]);
                    }
                } catch (e) {
                    console.error("OneOrder API Error:", e);
                    setMySubscriptionList([]);
                }
            };

            fetchOneOrder();
        }
    }, [activeTab]);
    // ▲▲▲ ------------------------------------------- ▲▲▲

    const now = new Date();
    const parseDateTime = (dateStr, timeStr) => {
        try {
            let time = timeStr;
            const dt = new Date(`${dateStr} ${time}`);
            if (isNaN(dt.getTime())) return new Date(dateStr);
            return dt;
        } catch (e) {
            return new Date();
        }
    };

    const activeReserveList = myPtList.filter(item => {
        const itemDate = parseDateTime(item.date, item.time);
        const isFuture = itemDate >= now;
        const isActiveStatus = item.status !== '취소됨' && item.status !== '수강완료';
        return isFuture && isActiveStatus;
    }).sort((a, b) => {
        const dateA = parseDateTime(a.date, a.time);
        const dateB = parseDateTime(b.date, b.time);
        return dateA - dateB;
    });

    const pastReserveList = myPtList.filter(item => {
        const itemDate = parseDateTime(item.date, item.time);
        const isPast = itemDate < now;
        const isDoneStatus = item.status === '취소됨' || item.status === '수강완료';
        return isPast || isDoneStatus;
    }).sort((a, b) => {
        const dateA = parseDateTime(a.date, a.time);
        const dateB = parseDateTime(b.date, b.time);
        return dateB - dateA;
    });

    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        measureHeight();
        let animationFrame;
        const debouncedMeasure = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };
        window.addEventListener('resize', debouncedMeasure);
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }, []);

    const getTabButtonStyle = (tabName) => ({
        text: {
            ...staticStyles.tabButtonTextBase,
            ...(tabName === activeTab ? staticStyles.tabButtonTextActive : staticStyles.tabButtonTextInactive)
        },
        indicator: {
            ...staticStyles.tabButtonIndicatorBase,
            ...(tabName === activeTab ? staticStyles.tabButtonIndicatorActive : staticStyles.tabButtonIndicatorInactive)
        }
    });

    return (
        <div style={staticStyles.backgroundWrapper}>
            <main style={staticStyles.mainContent}>
                <div style={staticStyles.branchTitleContainer}>
                    <h2 style={staticStyles.branchTitle}>{brName}</h2>
                </div>

                <div style={staticStyles.tabSelectContainer}>
                    <button onClick={() => setActiveTab('PT')} style={staticStyles.tabButtonBase}>
                        <span style={getTabButtonStyle('PT').text}>PT</span>
                        <div style={getTabButtonStyle('PT').indicator} />
                    </button>
                    <button onClick={() => setActiveTab('MY_PT')} style={staticStyles.tabButtonBase}>
                        <span style={getTabButtonStyle('MY_PT').text}>나의 PT</span>
                        <div style={getTabButtonStyle('MY_PT').indicator} />
                    </button>
                </div>

                <div style={staticStyles.contentArea}>
                    {activeTab === 'PT' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {/* 트레이너 목록 영역 주석 처리함
                            <div style={staticStyles.coachCardList}>
                                <div style={{ fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF', marginBottom: '8px' }}>
                                    트레이너 목록
                                </div>
                                {isLoading ? (
                                    <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                                        로딩 중...
                                    </div>
                                ) : coachList.length > 0 ? (
                                    coachList.map((coach, index) => (
                                        <CoachCard
                                            key={coach.mnKey || coach.id || index}
                                            coach={coach}
                                            onClick={handleCoachClick}
                                        />
                                    ))
                                ) : (
                                    <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px' }}>
                                        문의 가능한 트레이너가 없습니다.
                                    </div>
                                )}
                            </div>
                            */}

                            {/* PT 스케줄 영역 */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* <div style={{ fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF', marginBottom: '8px' }}>
                                    PT 수업 예약
                                </div> */}
                                {isLoadingSchedule ? (
                                    <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                                        로딩 중...
                                    </div>
                                ) : scheduleList.length > 0 ? (
                                    scheduleList.map((day, index) => (
                                        <DaySection
                                            key={day.date ? `day-${day.date}-${index}` : `idx-${index}`}
                                            day={day}
                                            onClassClick={(ex) => {
                                                navigate('/pt/detail', {
                                                    state: {
                                                        mnKey: ex.ctKey,
                                                        stKind: stKind,
                                                        brKey: brKey,
                                                        trainer: coachList.find(c => (c.mnKey || c.id) === ex.ctKey) || { coachName: ex.exerciseName, img: ex.img },
                                                        date: day.date
                                                    }
                                                });
                                            }}
                                            onTimeClick={handleTimeSlotClick}
                                        />
                                    ))
                                ) : (
                                    <div style={{ color: '#9A9A9A', textAlign: 'center', padding: '20px' }}>
                                        예약 가능한 PT 수업 일정이 없습니다.
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* [진행 중인 PT 섹션] */}
                            <div className="MySubProd" style={staticStyles.mySubProdContainer}>
                                <div style={staticStyles.mySubProdHeader}>진행 중인 PT</div>
                                {mySubscriptionList.length > 0 ? (
                                    mySubscriptionList.map((sub, index) => (
                                        <MySubscriptionCard key={sub.id || index} item={sub} />
                                    ))
                                ) : (
                                    <div style={{
                                        color: '#9A9A9A', textAlign: 'center', padding: '40px 0',
                                        width: '100%', display: 'flex', justifyContent: 'center',
                                        alignItems: 'center', fontFamily: 'Pretendard', fontSize: '14px'
                                    }}>
                                        진행 중인 상품이 없습니다.
                                    </div>)}
                            </div>

                            <MyReserveSection
                                list={activeReserveList}
                                onMoreClick={handleMoreReservations}
                            />

                            <PastReserveSection
                                list={pastReserveList}
                            />
                        </>)}
                </div>
                <Footer />
            </main>

            {/* 바텀시트 및 모달 */}
            {showSheet && (
                <BottomSheet
                    show={showSheet}
                    onClose={() => {
                        setShowSheet(false);
                        setInitialTimeData(null);
                    }}
                    availableTimes={sheetTimes}
                    onDateChange={(date) => handleDateChange(date)}
                    onConfirm={handleSheetConfirm}
                    selectedDate={currentSelectedDate}
                    initialData={initialTimeData}
                    brName={brName}
                />
            )}

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleFinalReservation}
                title="수업 예약"
                message={`${pendingReservation?.date} ${pendingReservation?.time}\n예약하시겠습니까?`}
            />

            {/* 예약 권한 부족 모달 */}
            {alertModal.isOpen && (
                <ConfirmModal
                    isOpen={alertModal.isOpen}
                    onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                    onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
                    title={alertModal.title}
                    message={alertModal.message}
                    confirmText="확인"
                />
            )}
        </div>
    );
}

const scheduleStyles = {
    dayContainer: { width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' },
    dayHeader: { width: '100%', padding: '0 4px' },
    dayLabel: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#FFFFFF' },
    classList: { display: 'flex', flexDirection: 'column', gap: '16px' },
    classCard: {
        width: '100%', background: '#1B1931', borderRadius: '10px', padding: '16px', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: '16px', cursor: 'pointer'
    },
    classInfoMain: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    classImg: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 },
    classTextContent: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
    className: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FCFCFA', margin: 0 },
    classIntro: {
        fontFamily: 'Pretendard', fontWeight: 400, fontSize: '13px', color: '#9A9A9A', margin: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
    },
    timeSlotList: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    timeSlot: {
        background: '#0D042D', border: '1px solid #8052FF', borderRadius: '6px',
        padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    timeSlotDisabled: { background: '#333333', border: '1px solid #555555', opacity: 0.6 },
    timeSlotText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF' }
};

// 스타일 관련 코드는 기존과 동일하므로 생략 (myReserveStyles, staticStyles 유지)
const myReserveStyles = {
    container: {
        width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '48px',
    },
    titleWrapper: {
        display: 'flex', alignItems: 'center', gap: '8px',
        cursor: 'pointer', width: '100%',
    },
    title: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF',
    },
    arrowIcon: {
        width: '7px', height: '14px', color: '#FCFCFA', marginLeft: 'auto',
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '12px',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        width: 'calc(100% + 48px)',
        marginLeft: '-24px',
        marginRight: '-24px',
        padding: '0 24px',
        boxSizing: 'border-box',
    },
    card: {
        minWidth: 'calc(100vw - 48px)',
        flexShrink: 0,
        scrollSnapAlign: 'center',
        height: '92px',
        borderRadius: '10px', background: '#1B1931',
        display: 'flex', alignItems: 'center', padding: '16px', boxSizing: 'border-box',
        cursor: 'pointer',
    },
    coachImg: {
        width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', marginRight: '16px',
    },
    infoContainer: {
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px',
    },
    coachName: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '16px', color: '#FCFCFA',
    },
    dateTimeInfo: {
        display: 'flex', alignItems: 'center', gap: '8px',
    },
    dateWrapper: { display: 'flex', alignItems: 'center', gap: '4px' },
    timeWrapper: { display: 'flex', alignItems: 'center', gap: '4px' },
    icon: { width: '15px', height: '15px', color: '#9A9A9A' },
    dateText: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF',
    },
    timeText: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', color: '#FFFFFF',
    },
    dotDivider: {
        width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#FFFFFF50',
    },
    emptyState: {
        color: '#9A9A9A', textAlign: 'center', padding: '20px', fontSize: '14px',
    }
};

/** @type {React.CSSProperties} */
const staticStyles = {
    // ... (기존 스타일 유지) ...
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        margin: '0 auto', boxSizing: 'border-box', overflowX: 'hidden',
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },

    mainContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '19px',
        padding: '0 24px 100px 24px',
    },
    branchTitleContainer: {
        width: '100%',
        textAlign: 'left',
    },
    branchTitle: {
        fontFamily: 'Pretendard',
        fontWeight: '700',
        fontSize: '18px',
        color: '#FCFCFA',
        margin: 0,
        lineHeight: '1.3',
    },
    tabSelectContainer: {
        width: '100%', height: '41px', borderBottom: '1px solid #1D1A3E',
        display: 'flex', alignItems: 'flex-start', gap: '24px',
        boxSizing: 'border-box', flexShrink: 0, marginBottom: '19px',
    },
    tabButtonBase: {
        background: 'transparent', border: 'none', padding: 0, margin: 0, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '41px',
    },
    tabButtonTextBase: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '18px', lineHeight: '17px', verticalAlign: 'middle',
    },
    tabButtonTextActive: { color: '#FFFFFF' },
    tabButtonTextInactive: { color: '#747474' },
    tabButtonIndicatorBase: {
        marginTop: '10px', width: '100%', height: '3px', borderRadius: '1.5px', transition: 'background-color 0.3s',
    },
    tabButtonIndicatorActive: {
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
    },
    tabButtonIndicatorInactive: { background: 'transparent' },
    contentArea: {
        width: '100%',
        display: 'flex', flexDirection: 'column',
    },
    coachCardList: {
        width: '100%', borderRadius: '10px', display: 'flex', flexDirection: 'column',
        gap: '16px', marginBottom: '24px',
    },
    ptCoachCard: {
        width: '100%', height: '135px', borderRadius: '10px', background: '#1B1931',
        display: 'flex', overflow: 'hidden', cursor: 'pointer',
        border: '1px solid transparent',
    },
    ptCoachCardImageContainer: {
        width: '124px', height: '100%', flexShrink: 0,
    },
    ptCoachCardImage: {
        width: '100%', height: '100%', objectFit: 'cover',
    },
    ptCoachCardInfo: {
        flex: 1, // 남은 공간 꽉 채우기
        padding: '21px 16px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        minWidth: 0,
    },
    coachName: {
        fontFamily: 'Pretendard', fontWeight: '600', fontSize: '16px', lineHeight: '17px',
        color: '#FCFCFA', margin: 0, marginBottom: '8px',
    },
    coachIntroduction: {
        fontFamily: 'Pretendard', fontWeight: '500', fontSize: '14px', lineHeight: '21px',
        color: '#9A9A9A', margin: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        height: '42px', // 2줄 높이 고정
    },
    coachReview: {
        display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'auto',
    },
    starDiv: {
        display: 'flex', alignItems: 'center', gap: '4px',
    },
    starIcon: { width: '14px', height: '14px' },
    rating: {
        fontFamily: 'Pretendard', fontWeight: '600', fontSize: '14px', lineHeight: '100%', color: '#FCFCFA',
    },
    dotIcon: { width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#FCFCFA' },
    reviewCount: {
        fontFamily: 'Pretendard', fontWeight: '400', fontSize: '12px', lineHeight: '100%', color: '#FCFCFA',
    },
    bottomNavWrapper: { flexShrink: 0 },

    // --- 나의 구독 카드 스타일 (유지) ---
    mySubProdContainer: { width: '100%', display: 'flex', flexDirection: 'column', gap: '18px' },
    mySubProdHeader: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '20px', color: '#FFFFFF' },
    mySubProdBox: {
        width: '100%', background: '#FCFCFA66', border: '1px solid #9A9A9A', borderRadius: '10px', overflow: 'hidden'
    },
    mySubProdTitle: {
        width: '100%',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        padding: '20px',
        boxSizing: 'border-box'
    },
    mySubProdName: {
        display: 'flex',
        alignItems: 'center',
        fontFamily: 'Pretendard',
        fontWeight: 600,
        fontSize: '16px',
        color: '#FCFCFA',
        marginBottom: '20px'
    },
    crownIcon: { width: '20px', height: '18px', marginLeft: '8px' },
    mySubState: {
        marginLeft: 'auto',
        borderRadius: '20px',
        border: '1px solid #FCFCFA',
        padding: '4px 12px',
        fontSize: '12px',
        color: '#FCFCFA'
    },
    subPeriod: {
        display: 'flex',
        justifyContent: 'space-between',
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: '15px',
        color: '#FCFCFA',
    },
    subPeriodValue: { fontFamily: 'Pretendard', fontWeight: 600 },
    cardDivider: { width: '100%', height: '1px', background: '#AEAEAE', margin: '10px 0' },
    // ▲▲▲ -------------------------- ▲▲▲
};

export default Pt;